import type {
  DashboardDomainRegistryItem,
  DashboardFilterDefinitionItem,
} from '@/types/DashboardDomainRegistry.types'

/**
 * sharedFilters — partition selected domains' filters into a shared pool
 * (rendered once at the top of FiltersBlock) and per-domain pools (rendered
 * inside each domain card).
 *
 * Per OQ-3 (TDD §3.1): dedup is FE-side, scoped to a single appCode. The
 * canonical example is `season` — every Campminder dashboard-era domain
 * declares it with `sharedAcrossDomains: true`, and the user picks it once
 * regardless of how many CampMinder-era domains they selected.
 *
 * Single-domain selection still benefits from the partition: a "shared"
 * filter renders at the top of the layout instead of inside the domain card,
 * which is the visual treatment the mockups specify.
 */

export interface SharedFilterEntry {
  filter: DashboardFilterDefinitionItem
  appCode: string
  /**
   * The first selected domain that declares this shared filter. Its
   * qualifiedDomainId drives discovery dispatch; its pickerKey drives Redux
   * state-key resolution for selectedValues.
   */
  primaryQualifiedId: string
  primaryPickerKey: string
}

export interface PartitionedFilters {
  sharedFilters: SharedFilterEntry[]
  /** Maps pickerKey → that domain's filters, with shared entries removed. */
  perDomainFilters: Map<string, DashboardFilterDefinitionItem[]>
}

/**
 * Partition every selected domain's filter list. A filter is "shared" when its
 * `sharedAcrossDomains` flag is true; the partition collapses duplicates
 * keyed by `(appCode, filterCode)`.
 *
 * Order:
 *  - sharedFilters → sorted by `filter.order ?? 0` ascending.
 *  - perDomainFilters per domain → preserves the registry-declared order
 *    (which is also `filter.order` ascending — applied by the BE projector).
 */
export function partitionSharedAndPerDomainFilters(
  selectedDomains: DashboardDomainRegistryItem[],
): PartitionedFilters {
  const sharedKeys = new Set<string>() // `${appCode}:${filterCode}`
  const sharedEntries = new Map<string, SharedFilterEntry>()
  const perDomain = new Map<string, DashboardFilterDefinitionItem[]>()

  // Pass 1: collect candidate shared keys.
  // Single domain still benefits — even a single occurrence of a
  // sharedAcrossDomains=true filter promotes it out of the domain card.
  for (const domain of selectedDomains) {
    for (const f of domain.filters) {
      if (f.sharedAcrossDomains) {
        sharedKeys.add(`${domain.appCode}:${f.code}`)
      }
    }
  }

  // Pass 2: build entries.
  for (const domain of selectedDomains) {
    const perDomainList: DashboardFilterDefinitionItem[] = []
    for (const f of domain.filters) {
      const key = `${domain.appCode}:${f.code}`
      if (sharedKeys.has(key) && f.sharedAcrossDomains) {
        if (!sharedEntries.has(key)) {
          sharedEntries.set(key, {
            filter: f,
            appCode: domain.appCode,
            primaryQualifiedId: domain.qualifiedDomainId,
            primaryPickerKey: domain.pickerKey,
          })
        }
      } else {
        perDomainList.push(f)
      }
    }
    perDomain.set(domain.pickerKey, perDomainList)
  }

  return {
    sharedFilters: Array.from(sharedEntries.values()).sort(
      (a, b) => (a.filter.order ?? 0) - (b.filter.order ?? 0),
    ),
    perDomainFilters: perDomain,
  }
}
